// Lane B4's assessment and framework words (S11): the Assessment card in the interview-plan editor, the candidate's
// assessment chain and the framework page under Settings. Only the words the design system's own label modules do not
// already carry; the screens import those (the status chain, Not Required · Required, the reasons, the pinned
// Interview recommendations field, Owner, Outcome, Evidence, Rating, Executive participation, the level names, the
// round names). Each entry quotes the vendored glossary line it is taken from; labels.test.mjs checks every quote, that
// the text sits inside it, that his words come from the glossary, and that nothing here is pending (none is).
//
// Words the screens need and his text does not hold yet are not drawn (recorded as D0 change requests in the slice
// report): "recorded, not sent", "reviews n of m", "not returned", "proceeded without the assessment", the verbs to
// withdraw and to add a criterion, the finding words of a criterion, the brief's own field name and a name for the
// framework's assessment default.

import { defineLabels, type LabelDef, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;

/** G §6 "Assessment / case study": his step 12 noun and its decision sentence. */
const TERM = "Assessment / case study";
const DECIDE = "\"HM + Recruiter decide at intake: required?, type, stage, owner, reviewers and criteria.\"";
const CAPTURE = "\"Capture sent/due/completed/outcome.\"";

/** The card, the chain and the decision's field names (CR-022). */
export const ASSESSMENT_WORDS = /* @__PURE__ */ defineLabels({
  assessment: his("Assessment"),
  /** The default assessment type the decision offers ("case study" — D13 default). */
  caseStudy: his("case study"),
  /** The brief the candidate receives: his noun for the work itself. */
  brief: his("Case study"),
  type: his("Type"),
  stage: his("Stage"),
  reviewers: his("Reviewers"),
  criteria: his("Criteria"),
  due: his("Due"),
  outcome: his("Outcome"),
  /** The one primary of a chain step (Sent, Submitted) and of a dialog that records it. */
  record: his("Record"),
  /** A reviewer or a criterion taken off the decision (an icon button's name). */
  remove: his("Remove"),
});

/** The framework page's words (CR-019, CR-066): the level's minimum skills and the assessment default's middle choice.
 * His BRD sentence "required/recommended/optional" is not in the vendored glossary; the word is quoted from G §5. */
export const FRAMEWORK_WORDS = /* @__PURE__ */ defineLabels({
  requiredSkills: his("Required skills"),
  recommended: his("Recommended"),
});
